<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .cta-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: utils.in_rem(4px);

    .cta-label {
      overflow: hidden;
      text-overflow: ellipsis;
      color: colors.get(textBlack);
      @include mixins.body4();
    }
  }
</style>

<script>
  // Svelte
  import { getContext } from "svelte";
  // Stores
  import {
    teamHasDiallerStore,
    userStore,
  } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import ButtonWithTooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/ButtonWithTooltip.svelte";
  import IconScale from "@sourcewhale/shared/svelte-design-system/components/atoms/IconScale.svelte";
  import Toggletip from "@sourcewhale/shared/svelte-design-system/components/atoms/Toggles/Toggletip.svelte";
  import Dropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/Dropdown.svelte";
  import ListItem from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/ListItem.svelte";
  import MoreInfo from "./MoreInfo.svelte";
  import { hasConversationsLicense } from "@sourcewhale/core";
  import { diallerState } from "@sourcewhale/shared/src/svelte/src/pages/Dialler/diallerState.svelte.js";
  // Context
  import { CONTACT_CTAS_CONTEXT } from "./context";

  // Get store from context
  const { candidateStore, candidatePhoneStore, callAnsweredStore, itemStore } =
    getContext(CONTACT_CTAS_CONTEXT);

  let {
    kind = "secondary",
    size = "candidate-cta",
    leadingIcon = "",
    wrapper = true,
    style = "",
    tooltipPlacement = "top",
    dropdownPlacement = "bottom",
    children,
  } = $props();

  let closeDropdown = $state(null);

  const makeListItemClick = (phoneNum) => () => {
    if (closeDropdown) closeDropdown();
    if (phoneNum) {
      const candidate = {
        candidateId: $candidateStore.candidateId,
        name: $candidateStore.name,
        photo: $candidateStore.photo,
        phone: phoneNum,
        jobTitle: $candidateStore.role,
        company: $candidateStore.company,
        role: "candidate",
      };

      const user = {
        id: $userStore.userId,
        name: $userStore.name,
        photo: $userStore.photo,
        jobTitle: $userStore.role,
        company: $userStore.company,
        role: "user",
      };

      // Only give dialler a todoId if it's not the contact modal and not an AI todo
      const todoId =
        $itemStore?.toDoId &&
        ["aiToDo", "opportunity"].includes($itemStore?.kind)
          ? $itemStore.toDoId
          : undefined;

      diallerState.makeCall([candidate, user], todoId);

      $callAnsweredStore[phoneNum] = true;
    }
  };

  const iconCls = (phoneObj) =>
    [
      "fa-regular",
      phoneObj.kind === "mobile" ? "fa-mobile" : "fa-phone",
      $callAnsweredStore[phoneObj.phone] ? "color-accentStatusSeaGreen" : "",
    ]
      .filter(Boolean)
      .join(" ");
</script>

{#snippet button()}
  {#if $candidatePhoneStore.length === 0 || $candidateStore.isDeleted || !hasConversationsLicense($userStore)}
    <ButtonWithTooltip
      {kind}
      {style}
      class="disabled"
      {leadingIcon}
      trailingIcon=""
      {size}
      placement={tooltipPlacement}
    >
      <span slot="tooltip">
        {hasConversationsLicense($userStore)
          ? "No phone numbers available."
          : "Using Dialer requires a Conversations license."}
      </span>
      {@render children?.()}
    </ButtonWithTooltip>
  {:else if !$teamHasDiallerStore}
    <Toggletip let:toggle>
      <MoreInfo
        slot="toggletip"
        info="Want to make a call? Click here to learn more about Dialer."
        buttonText="Learn more"
        href="https://sourcewhale.com/dialer/"
      />
      <Button
        {kind}
        style="cursor: pointer;{style}"
        class=""
        {leadingIcon}
        trailingIcon=""
        onclick={toggle}
        {size}
      >
        {@render children?.()}
      </Button>
    </Toggletip>
  {:else if $candidatePhoneStore.length === 1}
    {@const phoneObj = $candidatePhoneStore[0] || {}}
    <ButtonWithTooltip
      {kind}
      {style}
      placement={tooltipPlacement}
      class=""
      {leadingIcon}
      trailingIcon=""
      {size}
      onclick={makeListItemClick(phoneObj.phone)}
    >
      <span slot="tooltip">Call {phoneObj.phone}</span>
      {#if children}
        {@render children()}
      {:else}
        <IconScale class={iconCls(phoneObj)} size="medium" />
      {/if}
    </ButtonWithTooltip>
  {:else}
    <Dropdown
      {kind}
      {style}
      class=""
      {leadingIcon}
      trailingIcon=""
      {size}
      dynamicWidth={false}
      placement={dropdownPlacement}
      bind:close={closeDropdown}
    >
      <span slot="button">{@render children?.()}</span>
      {#each $candidatePhoneStore as phoneObj}
        <ListItem
          onclick={makeListItemClick(phoneObj.phone)}
          icon={iconCls(phoneObj)}
        >
          {phoneObj.phone}
        </ListItem>
      {/each}
    </Dropdown>
  {/if}
{/snippet}

{#if wrapper}
  <div class="cta-wrapper">
    {@render button()}
    <span class="cta-label">Call</span>
  </div>
{:else}
  {@render button()}
{/if}
